import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { CreateRole, GetRole, getRoles, UpdateRole } from "api/config/roles";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { KeyValuePair } from "utils/common";
import { required } from "utils/formstate";
import { isDeepEqual } from "utils/utils";
import { RoleConfigDto, RoleListItem } from "views/config/roles/RoleConfigDto";

export class RoleStore {
  root: RootStore;
  version: string = "";
  dataLoading = false;
  dataLoadingError: ErrorResponseDto | null = null;
  workgroups: KeyValuePair[] = [];
  allAssetTypes: KeyValuePair[] = [];
  allOwners: KeyValuePair[] = [];
  allRoles: RoleListItem[] = []
  currentRoleId: string | undefined = undefined;
  currentRole: RoleListItem = new RoleListItem();
  roleForm = this.createRoleForm();
  saveLoading: boolean = false;

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      dataLoading: observable,
      saveLoading: observable,
      dataLoadingError: observable,
      workgroups: observable,
      roleForm: observable,
      currentRoleId: observable,
      allRoles: observable,

      LoadData: action.bound,
      reset: action.bound,
      SaveRole: action.bound,

      isAnyChange: computed,
    });
  }

  setCurrentRoleId = (roleId: string | undefined) => {
    this.currentRoleId = roleId;
  }

  createRoleForm() {
    return new FormState({
      workgroupId: new FieldState(this.currentRole?.workgroup.id).validators(required),
      name: new FieldState(this.currentRole.name).validators(required, (value) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        if (this.allRoles.some(role => role.id !== this.currentRoleId
          && role.name.toLocaleLowerCase() === value.toLocaleLowerCase()))
          return "A role with the name specified already exists."
      }),
      owners: new FieldState<string[]>(this.currentRole.asset_owners || []),
      assetTypes: new FieldState<string[]>(this.currentRole.asset_types || []),
      assetPermissions: new FieldState(this.currentRole.asset_permissions || []),
      PeopleWorkgroups: new FieldState(this.currentRole.people_options.workgroup_ids || []),
      peoplePermissions: new FieldState(this.currentRole.people_options.permissions),
      devicePermissions: new FieldState(this.currentRole.device_permissions || []),
      adminPermissions: new FieldState(this.currentRole.workgroup_admin_permissions || []),
      securityPermissions: new FieldState(this.currentRole.security_permissions || []),
    });
  }

  fillForm(data: RoleListItem | null) {
    if (data) {
      this.currentRole = data;
    }
    this.roleForm = this.createRoleForm();
    this.roleForm.validate();
  }

  reset() {
    this.currentRole = new RoleListItem();
    this.roleForm = this.createRoleForm();
    this.dataLoadingError = null
  }

  LoadData(roleId: string | null = null) {
    this.dataLoading = true;
    this.dataLoadingError = null
    this.workgroups = this.root.userRolePermissionStore.getWorkgroupsWithManageRolePermission
      .map(r => ({ key: r.name, value: r.id }))
    this.allAssetTypes = this.root.userRolePermissionStore.AllAssetTypes
      .map(r => ({ key: r.label, value: r.value }));
    this.allOwners = this.root.userRolePermissionStore.AllAssetOwners
      .map(r => ({ key: r.label, value: r.value }));

    return Promise.all([
      getRoles(),
      roleId ? GetRole(roleId) : Promise.resolve(null),
    ])
      .then(res => {
        this.allRoles = res[0];
        this.fillForm(res[1]);
      })
      .catch((error) => {
        this.dataLoadingError = error
      })
      .finally(() => {
        this.dataLoading = false;
      })
  }

  NullOnEmptyList(list: any[]) {
    if (list.length === 0)
      return null
    return list
  }

  BuildRoleConfigDto() {
    const obj = new RoleConfigDto();
    obj.role.id = this.currentRole.id;
    obj.role.name = this.roleForm.$.name.value;
    obj.role.workgroup_id = this.roleForm.$.workgroupId.value;
    obj.role.security_permissions = this.NullOnEmptyList(this.roleForm.$.securityPermissions.value);

    obj.role.asset_permissions = this.NullOnEmptyList(this.roleForm.$.assetPermissions.value)
    obj.role.asset_types = this.NullOnEmptyList(this.roleForm.$.assetTypes.value)
    obj.role.asset_owners = this.NullOnEmptyList(this.roleForm.$.owners.value)

    obj.role.device_permissions = this.NullOnEmptyList(this.roleForm.$.devicePermissions.value)

    obj.role.people_options = {
      workgroup_ids: this.roleForm.$.peoplePermissions.value.length > 0 ?
        this.roleForm.$.PeopleWorkgroups.value : [],
      permissions: this.roleForm.$.peoplePermissions.value
    }
    obj.role.workgroup_admin_permissions = this.NullOnEmptyList(this.roleForm.$.adminPermissions.value)

    return obj.role;
  }

  async SaveRole(roleId: string | undefined) {
    this.saveLoading = true;
    const promise = roleId
      ? UpdateRole(roleId, this.BuildRoleConfigDto())
      : CreateRole(this.BuildRoleConfigDto())
    return promise
      .catch((error) => Promise.reject(error))
      .finally(() => this.saveLoading = false)
  }

  get isAnyChange() {
    const srcRole = this.currentRole;
    const finalRole = this.BuildRoleConfigDto();
    const debug = false;
    //............... always return true for create mode .....................
    if (!this.currentRoleId) {
      return false;
    }
    if (!isDeepEqual(srcRole, finalRole,
      ["workgroup", "workgroup_id", "asset_types", "asset_owners"], debug)) {
      return true;
    }
    if (!isDeepEqual(srcRole.asset_types, finalRole.asset_types,
      ["name", "is_enabled"], debug)) {
      return true;
    }
    if (!isDeepEqual(srcRole.asset_owners, finalRole.asset_owners,
      ["name", "is_enabled"], debug)) {
      return true;
    }
    return false;
  }
}
